import { FormEvent, useState } from 'react';
import { supabase } from './lib/supabase';

export type ListingImportDraft = {
  source_url?: string|null;
  primary_image_url?: string|null;
  vessel_name?: string|null;
  year?: number|null;
  make?: string|null;
  model?: string|null;
  hin?: string|null;
  registration_type?: string|null;
  registration_number?: string|null;
  sail_power?: string|null;
  loa?: string|null;
  beam?: string|null;
  draft?: string|null;
  location?: string|null;
  hull_material?: string|null;
  engine_summary?: string|null;
  port_engine_hours?: number|null;
  starboard_engine_hours?: number|null;
  generator_make_model?: string|null;
  generator_hours?: number|null;
  currency?: string|null;
  asking_price?: number|null;
  marketing_blurb?: string|null;
  seller?: { name?:string|null; email?:string|null; phone?:string|null; address?:string|null; city?:string|null; province?:string|null; postal?:string|null }|null;
  specifications?: Record<string,unknown>;
  extra_details?: Array<{label:string;value:string}>;
  warnings?: string[];
};

export default function ListingImporter({organizationId,onUse,onCancel}:{organizationId:string;onUse:(draft:ListingImportDraft)=>void;onCancel:()=>void}) {
  const [sourceMode,setSourceMode]=useState<'url'|'text'>('url');
  const [sourceUrl,setSourceUrl]=useState('');
  const [sourceText,setSourceText]=useState('');
  const [busy,setBusy]=useState(false);
  const [error,setError]=useState('');
  const [draft,setDraft]=useState<ListingImportDraft|null>(null);

  async function analyze(event:FormEvent) {
    event.preventDefault();
    if (!supabase || busy) return;
    setBusy(true); setError(''); setDraft(null);
    const {data,error:fnError}=await supabase.functions.invoke('marine-import-listing',{body:{action:'preview',organization_id:organizationId,source_url:sourceMode==='url'?sourceUrl.trim():null,pasted_text:sourceMode==='text'?sourceText.trim():null}});
    setBusy(false);
    if (fnError || data?.error) {setError(fnError?.message||String(data?.error||'Unable to read this listing.'));return;}
    setDraft(data?.draft||null);
  }

  const title=draft?[draft.year,draft.make,draft.model].filter(Boolean).join(' '):'';
  return <div className="listing-importer">
    {!draft?<form onSubmit={analyze}>
      <div className="import-mode-toggle"><button type="button" className={sourceMode==='url'?'active':''} onClick={()=>setSourceMode('url')}>Listing URL</button><button type="button" className={sourceMode==='text'?'active':''} onClick={()=>setSourceMode('text')}>Paste listing text</button></div>
      {sourceMode==='url'?<label>Public listing URL<input type="url" value={sourceUrl} onChange={(e)=>setSourceUrl(e.target.value)} placeholder="https://..." required/></label>:<label>Listing text<textarea rows={12} value={sourceText} onChange={(e)=>setSourceText(e.target.value)} placeholder="Paste the listing description/specifications here…" required/></label>}
      <p className="import-helper">MarineOps reads the listing and prepares a draft. Nothing is created until you review the information and finish the normal listing intake.</p>
      {error&&<div className="auth-alert error">{error}</div>}
      <div className="platform-modal-actions"><button type="button" className="secondary-button" onClick={onCancel}>Cancel</button><button className="primary-button" disabled={busy}>{busy?'Reading listing…':'Read listing'}</button></div>
    </form>:<div className="import-preview">
      <div className="import-preview-hero">{draft.primary_image_url?<img src={draft.primary_image_url} alt={title||'Imported vessel'}/>:<div className="import-preview-placeholder">No photo found</div>}<div><p className="eyebrow">Import preview</p><h3>{title||'Listing details found'}</h3><strong>{draft.asking_price!=null?new Intl.NumberFormat('en-CA',{style:'currency',currency:draft.currency||'CAD',maximumFractionDigits:0}).format(draft.asking_price):'Price not found'}</strong><span>{draft.location||'Location not found'}</span></div></div>
      {draft.marketing_blurb&&<p className="import-blurb">{draft.marketing_blurb}</p>}
      <div className="import-preview-grid">
        <div><span>Vessel name</span><strong>{draft.vessel_name||'—'}</strong></div><div><span>HIN</span><strong>{draft.hin||'—'}</strong></div><div><span>LOA</span><strong>{draft.loa||'—'}</strong></div><div><span>Beam</span><strong>{draft.beam||'—'}</strong></div><div><span>Draft</span><strong>{draft.draft||'—'}</strong></div><div><span>Hull</span><strong>{draft.hull_material||'—'}</strong></div><div><span>Engines</span><strong>{draft.engine_summary||'—'}</strong></div><div><span>Registration</span><strong>{draft.registration_number||'—'}</strong></div>
      </div>
      {!!draft.extra_details?.length&&<div className="import-extra-grid">{draft.extra_details.slice(0,12).map((item,index)=><div key={`${item.label}-${index}`}><span>{item.label}</span><strong>{item.value}</strong></div>)}</div>}
      {!!draft.warnings?.length&&<div className="auth-alert warning"><strong>Review before creating</strong>{draft.warnings.map((warning,index)=><div key={index}>{warning}</div>)}</div>}
      <div className="platform-modal-actions"><button type="button" className="secondary-button" onClick={()=>setDraft(null)}>Back</button><button type="button" className="primary-button" onClick={()=>onUse(draft)}>Use this information</button></div>
    </div>}
  </div>;
}
